import type { Any } from '@/shared/constant/any';
import type { IApiTypeServerType } from '@/shared/constant/ApiType';
import { ApiType } from '@/shared/constant/ApiType';
import type { ICodeSample } from '@tarslib/types/lib/httpApi';
import {
  defaultSampleCode,
  SampleCodeAutoGenerateForType,
  type AdvancedSettings,
} from '@tarslib/types/lib/projectSetting';
import { v4 as uuid } from 'uuid';
import type { HttpApiRemoteType, HttpApiServerType } from './httpApi';

export enum CurlVariant {
  Window = 'cURL-Windows',
  Linux = 'cURL',
}

const languageMap: Record<
  string,
  {
    key: string;
    label: string;
    syntaxMode: string;
    variants: {
      id?: string;
      key: string;
      name?: string;
      prompt?: string;
      code?: string;
    }[];
  }
> = {
  shell: {
    key: 'shell',
    label: 'Shell',
    syntaxMode: 'powershell',
    variants: [
      {
        key: CurlVariant.Linux,
      },
      {
        key: CurlVariant.Window,
      },
      {
        key: 'Httpie',
      },
      {
        key: 'wget',
      },
      {
        key: 'PowerShell',
      },
    ],
  },
  javascript: {
    key: 'javascript',
    label: 'JavaScript',
    syntaxMode: 'javascript',
    variants: [
      {
        key: 'Fetch',
      },
      {
        key: 'Axios',
      },
      {
        key: 'jQuery',
      },
      {
        key: 'XHR',
      },
      {
        key: 'Native',
      },
      {
        key: 'Request',
      },
      {
        key: 'Unirest',
      },
    ],
  },
  java: {
    key: 'java',
    label: 'Java',
    syntaxMode: 'java',
    variants: [{ key: 'Unirest' }, { key: 'OkHttp' }],
  },
  swift: {
    key: 'swift',
    label: 'Swift',
    syntaxMode: 'swift',
    variants: [
      {
        key: 'URLSession',
      },
    ],
  },
  go: {
    key: 'go',
    label: 'Go',
    syntaxMode: 'go',
    variants: [
      {
        key: 'Native',
      },
    ],
  },
  php: {
    key: 'php',
    label: 'PHP',
    syntaxMode: 'php',
    variants: [
      {
        key: 'cURL',
      },
      {
        key: 'HTTP_Request2',
      },
      {
        key: 'pecl_http',
      },
      {
        key: 'Guzzle',
      },
    ],
  },
  python: {
    key: 'python',
    label: 'Python',
    syntaxMode: 'python',
    variants: [
      {
        key: 'http.client',
      },
      {
        key: 'Requests',
      },
    ],
  },
  http: {
    key: 'http',
    label: 'HTTP',
    syntaxMode: 'text',
    variants: [
      {
        key: 'HTTP',
      },
    ],
  },
  c: {
    key: 'c',
    label: 'C',
    syntaxMode: 'cpp',
    variants: [
      {
        key: 'libcurl',
      },
    ],
  },
  csharp: {
    key: 'csharp',
    label: 'C#',
    syntaxMode: 'csharp',
    variants: [
      {
        key: 'RestSharp',
      },
    ],
  },
  'objective-c': {
    key: 'objective-c',
    label: 'Objective-C',
    syntaxMode: 'objective-c',
    variants: [
      {
        key: 'NSURLSession',
      },
    ],
  },
  ruby: {
    key: 'ruby',
    label: 'Ruby',
    syntaxMode: 'ruby',
    variants: [
      {
        key: 'Net::HTTP',
      },
    ],
  },
  ocaml: {
    key: 'ocaml',
    label: 'OCaml',
    syntaxMode: 'ocaml',
    variants: [
      {
        key: 'Cohttp',
      },
    ],
  },
  dart: {
    key: 'dart',
    label: 'Dart',
    syntaxMode: 'dart',
    variants: [
      {
        key: 'http',
      },
    ],
  },
  r: {
    key: 'r',
    label: 'R',
    syntaxMode: 'r',
    variants: [
      {
        key: 'httr',
      },
      {
        key: 'RCurl',
      },
    ],
  },
};

function getLanguages(
  codeSamples: ICodeSample[],
  sampleCodeSettingParam: AdvancedSettings['sampleCode'],
) {
  const sampleCodeSetting = sampleCodeSettingParam ?? defaultSampleCode;
  const languageClone = Object.values(languageMap).map((x) => {
    return {
      ...x,
      variants: (() => {
        if (!sampleCodeSetting.enable) {
          return [];
        }
        const newVariants = x.variants.map((y) => ({
          ...y,
        }));
        if (
          sampleCodeSetting.autoGenerateFor === SampleCodeAutoGenerateForType.Custom &&
          sampleCodeSetting.customAutoGenerate
        ) {
          const findLanguageVariants = sampleCodeSetting.customAutoGenerate
            .find((y) => y.language === x.key)
            ?.children.filter((y) => y.isChecked)
            .map((y) => y.variant);
          if (!findLanguageVariants) {
            return [];
          }
          return newVariants.filter((y) => findLanguageVariants.includes(y.key));
        }

        return newVariants;
      })(),
    };
  });

  if (
    sampleCodeSetting.enable &&
    sampleCodeSetting.autoGenerateFor === SampleCodeAutoGenerateForType.Custom &&
    sampleCodeSetting.customAutoGenerate
  ) {
    languageClone.sort((a, b) => {
      const aIndex =
        sampleCodeSetting.customAutoGenerate?.findIndex((y) => y.language === a.key) ?? -1;
      const bIndex =
        sampleCodeSetting.customAutoGenerate?.findIndex((y) => y.language === b.key) ?? -1;

      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }
      if (aIndex !== -1) {
        return -1;
      }
      if (bIndex !== -1) {
        return 1;
      }
      return 0;
    });
    languageClone.forEach((x) => {
      const findLanguage = sampleCodeSetting.customAutoGenerate?.find((y) => y.language === x.key);
      if (findLanguage) {
        x.variants.sort((a, b) => {
          const aIndex = findLanguage.children.findIndex((y) => y.variant === a.key) ?? -1;
          const bIndex = findLanguage.children.findIndex((y) => y.variant === b.key) ?? -1;

          if (aIndex !== -1 && bIndex !== -1) {
            return aIndex - bIndex;
          }
          if (aIndex !== -1) {
            return -1;
          }
          if (bIndex !== -1) {
            return 1;
          }
          return 0;
        });
      }
    });
  }

  if (sampleCodeSetting.enable && !sampleCodeSetting.overwriteExisting) {
    codeSamples?.forEach((x) => {
      const findLanguage = languageClone.find((y) => y.key === x.language);
      if (findLanguage) {
        findLanguage.variants.splice(0, findLanguage.variants.length);
      }
    });
  }

  if (codeSamples) {
    let count = 0;
    [...codeSamples].reverse().forEach((x) => {
      const findLanguage = languageClone.find((y) => y.key === x.language);
      if (findLanguage) {
        const id = x.id ? String(x.id) : `${x.name}-${count++}`;
        findLanguage.variants.unshift({
          id,
          key: id,
          name: x.name,
          code: x.code,
          prompt: x.prompt,
        });
      }
    });
  }

  return languageClone.filter((x) => !!x.variants.length);
}

export function transformHttpApi(
  data: HttpApiRemoteType,
  sampleCodeSetting: AdvancedSettings['sampleCode'],
): HttpApiServerType {
  const {
    parameters,
    commonParameters,
    requestBody,
    codeSamples,
    responses,
    responseChildren = [],
    type,
    ...rest
  } = data;

  const query =
    parameters?.query?.map((x) => ({
      ...x,
      isCommonParameter: false,
      value: (x as typeof Any).sampleValue || x.example,
    })) ?? [];
  const path =
    parameters?.path?.map((x) => ({
      ...x,
      isCommonParameter: false,
      value: (x as typeof Any).sampleValue || x.example,
    })) ?? [];
  const cookie =
    parameters?.cookie?.map((x) => ({
      ...x,
      isCommonParameter: false,
      value: (x as typeof Any).sampleValue || x.example,
    })) ?? [];
  const header =
    parameters?.header?.map((x) => ({
      ...x,
      isCommonParameter: false,
      value: (x as typeof Any).sampleValue || x.example,
    })) ?? [];

  const newCodeSamples = codeSamples.filter((x) => x.id && languageMap[x.language]) ?? [];
  const languages = getLanguages(newCodeSamples, sampleCodeSetting);
  const newResponses = responses
    .map((x) => {
      return {
        ...x,
        headers:
          x.headers?.map((y) => ({
            ...y,
            isCommonParameter: false,
            value: (y as typeof Any).sampleValue || y.example,
          })) ?? [],
        key: uuid(),
        id: (() => {
          if (typeof x.id === 'number') {
            return x.id;
          }
          const idString = String(x.id).replace('tempId-', '');
          const idNumber = Number(idString);
          if (Number.isNaN(idNumber)) {
            return undefined;
          } else {
            return idNumber;
          }
        })(),
      };
    })
    .filter((x) => typeof x.id === 'number');
  const newResponseChildren = responseChildren.map((x) => String(x).replace('tempId-', ''));

  const requestMediaType = requestBody.mediaType || requestBody.type;
  const examples = (requestBody.examples ?? [])
    .filter((x) => x.mediaType === requestMediaType)
    .map((x, i) => {
      return {
        ...x,
        key: `${i}`,
      };
    });
  if (!examples.length) {
    const example = requestBody.data || requestBody.example || requestBody.sampleValue;
    if (example) {
      examples.push({
        value: example,
        description: '',
        mediaType: requestMediaType,
        key: uuid(),
      });
    }
  }

  return {
    ...rest,
    type: type as IApiTypeServerType,
    ...(data.type === ApiType.Webhook && { path: '' }), // webhook 文档不显示 app 设置的调试地址
    parameters: {
      query,
      path,
      cookie,
      header,
    },
    commonParameters: {
      query: commonParameters?.query ?? [],
      body: commonParameters?.body ?? [],
      header: commonParameters?.header ?? [],
      cookie: commonParameters?.cookie ?? [],
    },
    requestBody: {
      ...requestBody,
      examples,
      data:
        requestBody?.data ||
        requestBody?.sampleValue ||
        requestBody?.example ||
        examples[0]?.value ||
        '',
      parameters:
        requestBody?.parameters?.map((x) => ({
          ...x,
          isCommonParameter: false,
          value: (x as typeof Any).sampleValue || x.example,
        })) ?? [],
    },
    codeSamples: newCodeSamples,
    codeSampleLanguages: languages,
    responses: newResponses,
    oasExtensions: data.oasExtensions || '',
    responseChildren: newResponseChildren,
  };
}
